import { call, put, all, spawn, takeLatest } from 'redux-saga/effects';

import request from 'utils/request';
import { CUSTOMER_NOTIFICATIONS, AUDIT_HEADER } from 'utils/constants';
import { showToast } from '../Toast/actions';

import {
  handleAllEmailsResponse,
  disableLoaderAction,
  handleRemoveEmailAction,
  handleUpdateEmailChannelStatus,
  handleBulkEmailResponse,
  handleUpdateCustomerNotificationStatus,
  setErrorInAddingRecipient,
  handleUpdateCustomerRefundNotificationsStatus,
} from './actions';
import {
  GET_ALL_EMAILS,
  GET_ALL_EMAILS_URL,
  REMOVE_EMAIL,
  REMOVE_EMAIL_URL,
  UPDATE_EMAIL_CHANNEL_STATUS,
  UPDATE_EMAIL_CHANNEL_STATUS_URL,
  ADD_BULK_EMAIL,
  ADD_BULK_EMAIL_URL,
  EMAIL_LIST,
  IMPORT_EMAIL_LIST,
  GET_CUSTOMER_NOTIFICATIONS_URL,
  UPDATE_CUSTOMER_NOTIFICATIONS_STATUS,
  UPDATE_CUSTOMER_NOTIFICATIONS_STATUS_URL,
  GET_CUSTOMER_REFUND_NOTIFICATIONS,
  UPDATE_CUSTOMER_REFUND_NOTIFICATIONS_STATUS,
  REFUND_PARAMS,
  REFUND_EQUIVALENT_TYPE,
  UPDATE_REFUND_NOTIFICATIONS_STATUS_URL,
  GET_REFUND_NOTIFICATIONS_URL,
  UPDATE_EMAIL_URL,
} from './constants';

const SETTINGS_MERCHANT_NOTIF_AUDIT = 'settings-merchant-notifications';
const SETTINGS_CUSTOMER_NOTIF_AUDIT = 'settings-customer-notifications';

export function getURLBasedOnActiveNavigation(
  activeNavigation = '',
  isMerchantImportEnabled = false,
) {
  switch (activeNavigation) {
    case CUSTOMER_NOTIFICATIONS: {
      return GET_CUSTOMER_NOTIFICATIONS_URL;
    }
    default: {
      const { url, method } = GET_ALL_EMAILS_URL;
      const emailList = isMerchantImportEnabled
        ? IMPORT_EMAIL_LIST
        : EMAIL_LIST;
      const finalUrl = `${url}?emailType=${Object.keys(emailList).join(',')}`;
      return { url: finalUrl, method };
    }
  }
}

export function* getAllEmails({
  data: { activeNavigation, isMerchantImportEnabled },
}) {
  try {
    const { url, method } = getURLBasedOnActiveNavigation(
      activeNavigation,
      isMerchantImportEnabled,
    );
    const response = yield call(request, url, { method });
    yield put(handleAllEmailsResponse({ data: response, activeNavigation }));
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* getIndividualNotification(queryString) {
  try {
    const { url, method } = GET_REFUND_NOTIFICATIONS_URL;
    const options = {
      method,
      queryString,
    };
    const response = yield call(request, url, options);
    yield put(
      handleUpdateCustomerRefundNotificationsStatus({
        notificationTypeResponse: {
          ...response.notificationTypeResponse,
          isActive: response.isActive,
        },
      }),
    );
  } catch (err) {
    yield put(
      handleUpdateCustomerRefundNotificationsStatus({
        notificationTypeResponse: {
          notificationType: queryString.type,
          notificationChannel: queryString.channel,
          isActive: false,
        },
      }),
    );
  }
}
export function* getCustomerRefundNotifications() {
  try {
    yield all(
      REFUND_PARAMS.map(queryString =>
        call(getIndividualNotification, queryString),
      ),
    );
    yield put(disableLoaderAction());
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* removeEmail({ data: { data, activeNavigation } }) {
  try {
    const { url, method } = REMOVE_EMAIL_URL;
    const { email, type } = data;
    const finalUrl = `${url.replace('$email$', email)}?emailType=${type}`;
    const response = yield call(request, finalUrl, {
      method,
      headers: { [AUDIT_HEADER]: SETTINGS_MERCHANT_NOTIF_AUDIT },
    });
    yield put(handleRemoveEmailAction({ response, data, activeNavigation }));
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* updateEmailChannelStatus({
  data: { data, activeNavigation },
}) {
  try {
    const { url, method } = UPDATE_EMAIL_CHANNEL_STATUS_URL;
    const options = {
      method,
      body: JSON.stringify(data),
      headers: { [AUDIT_HEADER]: SETTINGS_MERCHANT_NOTIF_AUDIT },
    };
    const response = yield call(request, url, options);
    yield put(
      handleUpdateEmailChannelStatus({ data: response, activeNavigation }),
    );
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* updateCustomerNotifications({ data }) {
  try {
    const { url, method } = UPDATE_CUSTOMER_NOTIFICATIONS_STATUS_URL;
    const options = {
      method,
      body: JSON.stringify(data),
      headers: { [AUDIT_HEADER]: SETTINGS_CUSTOMER_NOTIF_AUDIT },
    };
    yield call(request, url, options);
    yield put(handleUpdateCustomerNotificationStatus({ data }));
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* updateCustomerRefundNotifications({ data }) {
  try {
    const { url, method } = UPDATE_REFUND_NOTIFICATIONS_STATUS_URL;
    const options = {
      method,
      body: JSON.stringify(data),
      headers: { [AUDIT_HEADER]: SETTINGS_CUSTOMER_NOTIF_AUDIT },
    };
    const response = yield call(request, url, options);

    const { notificationType, ...rest } = data;
    yield call(request, url, {
      ...options,
      body: JSON.stringify({
        ...rest,
        notificationType: REFUND_EQUIVALENT_TYPE[notificationType],
      }),
    });
    yield put(
      handleUpdateCustomerRefundNotificationsStatus({
        notificationTypeResponse: {
          ...response.notificationTypeResponse,
          isActive: response.isActive,
        },
      }),
    );
    yield put(disableLoaderAction());
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* addBulkEmail({ data: { addEmail, updateEmail, actions } }) {
  try {
    const urlArray = [ADD_BULK_EMAIL_URL, UPDATE_EMAIL_URL];
    yield all([
      addEmail.emailTypes?.length > 0 &&
        call(request, urlArray[0].url, {
          method: urlArray[0].method,
          body: JSON.stringify(addEmail),
          headers: { [AUDIT_HEADER]: SETTINGS_MERCHANT_NOTIF_AUDIT },
        }),
      updateEmail &&
        call(request, urlArray[1].url, {
          method: urlArray[1].method,
          body: JSON.stringify(updateEmail),
          headers: { [AUDIT_HEADER]: SETTINGS_MERCHANT_NOTIF_AUDIT },
        }),
    ]);
    yield put(handleBulkEmailResponse());
    actions.setSubmitting(false);
  } catch (err) {
    actions.setSubmitting(false);
    yield put(disableLoaderAction());
    if (
      err.message === 'This email ID is already added as a recipient' ||
      err.message ===
        'Maximum of 5 email IDs can be added. You can delete existing recipients and add new ones.'
    ) {
      // show error alert modal
      yield put(setErrorInAddingRecipient(err.message));
      return;
    }
    yield put(showToast(err));
  }
}

export function* getAllEmailsSaga() {
  yield takeLatest(GET_ALL_EMAILS, getAllEmails);
}

export function* removeEmailSaga() {
  yield takeLatest(REMOVE_EMAIL, removeEmail);
}

export function* updateEmailChannelStatusSaga() {
  yield takeLatest(UPDATE_EMAIL_CHANNEL_STATUS, updateEmailChannelStatus);
}

export function* updateCustomerNotificationsStatusSaga() {
  yield takeLatest(
    UPDATE_CUSTOMER_NOTIFICATIONS_STATUS,
    updateCustomerNotifications,
  );
}

export function* addBulkEmailSaga() {
  yield takeLatest(ADD_BULK_EMAIL, addBulkEmail);
}

export function* getCustomerRefundNotificationsSaga() {
  yield takeLatest(
    GET_CUSTOMER_REFUND_NOTIFICATIONS,
    getCustomerRefundNotifications,
  );
}

export function* updateCustomerRefundNotificationsStatusSaga() {
  yield takeLatest(
    UPDATE_CUSTOMER_REFUND_NOTIFICATIONS_STATUS,
    updateCustomerRefundNotifications,
  );
}

// Individual exports for testing
export default function* emailNotificationsSaga() {
  // See example in containers/HomePage/saga.js
  const sagas = [
    getAllEmailsSaga,
    removeEmailSaga,
    updateEmailChannelStatusSaga,
    addBulkEmailSaga,
    updateCustomerNotificationsStatusSaga,
    getCustomerRefundNotificationsSaga,
    updateCustomerRefundNotificationsStatusSaga,
  ];

  yield all(
    sagas.map(saga =>
      // eslint-disable-next-line func-names
      spawn(function*() {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }
      }),
    ),
  );
}
